import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Group,
  Stack,
  Text,
  Image,
  Box,
  Loader,
  Flex,
  Center,
  CopyButton,
  ActionIcon,
  Paper,
} from "@mantine/core";
import {
  IconBolt,
  IconHeart,
  IconLock,
  IconBuildingBank,
} from "@tabler/icons-react";

import { DataContext } from "../../../context/DataContext";
import { isMobile } from "react-device-detect";
import QRCode from "qrcode.react";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import DateOfBirthSelect from "../../../components/date-of-birth-select";

const DIGILOCKERStart = () => {
  const { settings, data, text } = useContext(DataContext);
  const [url, setUrl] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [dob, setDob] = useState({ day: "", month: "", year: "" });
  const [dobComplete, setDobComplete] = useState(false);
  const [step, setStep] = useState(1); // 1: intro, 2: dob input
  const [isLoading, setIsLoading] = useState(false);

  // Use edge status check when edge is true
  const isEdgeMode = data?.edge === true;
  useStatusCheck(!isEdgeMode && url !== null, false, "idmax");
  useEdgeStatusCheck(isEdgeMode && url !== null);

  const handleDobChange = (dobData) => {
    setDob(dobData);
    // Check if DOB is complete (all fields filled)
    const isComplete = dobData.day && dobData.month && dobData.year &&
                      dobData.day.length === 2 &&
                      dobData.month.length === 2 &&
                      dobData.year.length === 4;
    setDobComplete(isComplete);
  };

  const handleVerifyClick = () => {
    window.open(url, "_blank");
    //window.location.href = url;
  };

  const handleGetStarted = () => {
    setStep(2);
  };

  const handleContinue = () => {
    // Fetch URL when user clicks Continue
    if (!url && !isLoading) {
      setIsLoading(true);
      fetchUrl();
    }
  };

  // Polling function for edge mode
  const pollForUrl = async (requestId) => {
    const maxAttempts = 100; // 5 minutes = 300 seconds / 3 seconds per attempt
    const pollInterval = 3000; // 3 seconds
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch("https://dcams.app/edge/backend/api/idmax/retrieve", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ request_id: requestId }),
        });

        if (response.ok) {
          const result = await response.json();
          console.log("Poll response:", result);

          if (result.request_url) {
            setUrl(result.request_url);
            window.open(result.request_url, "_blank");
            return; // Success, stop polling
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          console.error("Polling timeout: No URL received after 5 minutes");
        }
      } catch (error) {
        console.error("Polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        }
      }
    };

    poll();
  };

  const fetchUrl = async () => {
    data.provider = "DIGILOCKER";
    // Add DOB to the data payload
    data.dob = `${dob.year}${dob.month}${dob.day}`;

    try {
      // Check if edge mode is enabled
      let apiUrl;
      let requestData = { ...data };

      if (data?.edge === true) {
        const edgeQueueUrl =
          process.env.REACT_APP_EDGE_QUEUE_URL ||
          "https://dcams.app/edge/frontend/api/queue/batch";
        apiUrl = edgeQueueUrl;
        // Add queue parameter for idmax
        requestData.queue = "idmax";
      } else {
        apiUrl = process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/idmax";
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data_returned = await response.json();
      console.log("data", data_returned);

      if (data?.edge === true) {
        // In edge mode, we get a request_id and need to poll for the URL
        const reqId = data_returned.request_id || data.request_id;
        if (reqId) {
          setRequestId(reqId);
          console.log("Starting polling for request_id:", reqId);
          pollForUrl(reqId);
        } else {
          console.error("No request_id found in edge response");
        }
      } else {
        // In normal mode, we get the URL directly
        const verifyUrl = data_returned.output.info.request_url;
        setUrl(verifyUrl);
        window.open(verifyUrl, "_blank");
      }
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  };


  return (
    <>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between", // This will push the button to the bottom
          height: "100%", // This should take the full height of its parent
        }}
      >
        <Box>
          <Stack>
            <Stack align="center" spacing="xs">
              <Group spacing={6}>
                <Image
                  width={35}
                  src="https://dcamsclientlogos.s3.us-east-2.amazonaws.com/digilocker.webp"
                />
                <Text fz="xl" fw={700}>
                  {text.digilocker?.title || "Verify With DigiLocker"}
                </Text>
              </Group>
              <Text fz="sm" style={{ color: "#4B5563" }}>
                {text.digilocker?.subtitle || "India's official digital document storage system"}
              </Text>
            </Stack>

            {/* Step 1: Introduction with bullet points */}
            {step === 1 && (
              <Center mt="md">
                <Paper p="lg" withBorder style={{ backgroundColor: "#fafafa", width: "100%" }}>
                  <Stack>
                    <Group noWrap>
                      <IconBolt size={20} />
                      <Text fz="md">{text.digilocker?.bulletPoints?.[0] || "Takes less than 2 minutes"}</Text>
                    </Group>
                    <Group noWrap>
                      <IconLock size={20} />
                      <Text fz="md">{text.digilocker?.bulletPoints?.[1] || "Safe and secure verification"}</Text>
                    </Group>
                    <Group noWrap>
                      <IconHeart size={20} />
                      <Text fz="md">{text.digilocker?.bulletPoints?.[2] || "No need to upload documents"}</Text>
                    </Group>
                  </Stack>
                </Paper>
              </Center>
            )}

            {/* Step 2: Date of Birth Input */}
            {step === 2 && (
              <Box mt="lg">
                <Box style={{ pointerEvents: isLoading ? 'none' : 'auto', opacity: isLoading ? 0.6 : 1 }}>
                  <DateOfBirthSelect
                    onDobChange={handleDobChange}
                    value={data.dob || ""}
                  />
                </Box>
              </Box>
            )}
          </Stack>
        </Box>
        <Box>
          {step === 1 && (
            <Stack spacing="md" style={{ width: '100%' }}>
              <Text align="center" size="sm" style={{ color: "#4B5563", lineHeight: 1.7 }}>
                {text.digilocker?.description || "You'll enter your date of birth, and we'll securely match it against your DigiLocker account to verify your age and identity."}
              </Text>
              <Button
                size="lg"
                onClick={handleGetStarted}
                fullWidth
              >
                Get Started
              </Button>
            </Stack>
          )}
          {step === 2 && (
            <Button
              size="lg"
              mt="lg"
              onClick={handleContinue}
              disabled={!dobComplete || isLoading}
              fullWidth
              leftIcon={isLoading ? <Loader size="sm" color="white" /> : null}
            >
              {isLoading ? (url ? "Checking your status..." : "Getting link...") : "Continue"}
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default DIGILOCKERStart;
